import { Link, useParams } from "@tanstack/solid-router";
import { Show, createEffect, createSignal } from "solid-js";

import { AppShell } from "~/components/AppShell";
import { CustomFieldsRenderer } from "~/components/CustomFieldsRenderer";
import { EntityTimeline } from "~/components/EntityTimeline";
import { TagBadges, TagsEditor } from "~/components/TagsEditor";
import {
  useArchiveCustomRecord,
  useCustomObjects,
  useCustomRecord,
  useUpdateCustomRecord,
} from "~/lib/hooks";
import type { CustomObjectDefinitionRead, CustomRecordUpdate } from "~/lib/types";

type FormState = {
  name: string;
  data: Record<string, unknown>;
  tags: string[];
};

export function CustomRecordDetailPage() {
  const params = useParams({ strict: false }) as () => {
    wsKey: string;
    objectKey: string;
    recordId: string;
  };

  // Resolve the object definition by URL key. Include archived so the page
  // still works after the parent object has been archived.
  const objects = useCustomObjects(
    () => params().wsKey,
    () => ({ include_archived: true }),
  );
  const matched = (): CustomObjectDefinitionRead | undefined =>
    objects.data?.items.find((o) => o.key === params().objectKey);

  return (
    <Show
      when={objects.data}
      fallback={
        <AppShell wsKey={params().wsKey} pageTitle="Record">
          <div class="py-12 text-center opacity-60">Loading…</div>
        </AppShell>
      }
    >
      <Show
        when={matched()}
        fallback={
          <AppShell wsKey={params().wsKey} pageTitle="Record">
            <div class="py-12 text-center">
              <p class="text-lg font-semibold">No object named "{params().objectKey}"</p>
            </div>
          </AppShell>
        }
      >
        {(obj) => (
          <Body wsKey={() => params().wsKey} obj={obj()} recordId={() => params().recordId} />
        )}
      </Show>
    </Show>
  );
}

function Body(props: {
  wsKey: () => string;
  obj: CustomObjectDefinitionRead;
  recordId: () => string;
}) {
  const record = useCustomRecord(
    props.wsKey,
    () => props.obj.id,
    () => props.recordId(),
  );
  const updateMutation = useUpdateCustomRecord(props.wsKey, () => props.obj.id);
  const archiveMutation = useArchiveCustomRecord(props.wsKey, () => props.obj.id);

  const [editing, setEditing] = createSignal(false);
  const [form, setForm] = createSignal<FormState>({ name: "", data: {}, tags: [] });
  const [error, setError] = createSignal<string | null>(null);

  // Hydrate the form when entering edit mode (or when the record data
  // refetches mid-edit, e.g. after a successful save).
  createEffect(() => {
    if (!editing()) return;
    const r = record.data;
    if (!r) return;
    setForm({
      name: r.name,
      data: (r.data ?? {}) as Record<string, unknown>,
      tags: r.tags ?? [],
    });
  });

  const cancel = () => {
    setEditing(false);
    setError(null);
  };

  const save = async (e: SubmitEvent) => {
    e.preventDefault();
    setError(null);
    const r = record.data;
    if (!r) return;
    const f = form();
    if (!f.name.trim()) return;
    const payload: CustomRecordUpdate = {
      name: f.name.trim(),
      data: Object.keys(f.data).length > 0 ? f.data : null,
      tags: f.tags.length > 0 ? f.tags : null,
    };
    try {
      await updateMutation.mutateAsync({
        recId: r.id,
        version: r.version,
        body: payload,
      });
      setEditing(false);
    } catch (err) {
      setError((err as Error).message);
    }
  };

  return (
    <AppShell wsKey={props.wsKey()} pageTitle={props.obj.label_singular}>
      <div class="flex flex-col gap-4">
        <div class="text-sm">
          <Link
            to="/workspaces/$wsKey/custom/$objectKey"
            params={{ wsKey: props.wsKey(), objectKey: props.obj.key }}
            class="link link-primary"
          >
            ← {props.obj.label_plural}
          </Link>
        </div>

        <Show when={record.isLoading}>
          <div role="status" aria-live="polite" class="text-center py-12 opacity-60">
            Loading…
          </div>
        </Show>

        <Show when={record.isError}>
          <div role="alert" class="alert alert-error">
            Failed to load record.
          </div>
        </Show>

        <Show when={record.data}>
          {(r) => {
            const isArchived = r().archived_at !== null;
            return (
              <>
                <div class="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <h1 class="text-2xl font-bold">{r().display_name || r().name}</h1>
                    <p class="text-xs opacity-60 mt-1">
                      Updated {new Date(r().updated_at).toLocaleString()}
                      <Show when={isArchived}>
                        <span class="badge badge-ghost badge-sm ml-2">archived</span>
                      </Show>
                    </p>
                    <Show when={(r().tags?.length ?? 0) > 0}>
                      <div class="mt-2">
                        <TagBadges tags={() => r().tags} size="sm" />
                      </div>
                    </Show>
                  </div>
                  <div class="flex gap-2">
                    <Show when={!editing() && !isArchived}>
                      <button
                        type="button"
                        class="btn btn-primary btn-sm"
                        onClick={() => setEditing(true)}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        class="btn btn-ghost btn-sm text-error"
                        onClick={() =>
                          archiveMutation.mutate({
                            recId: r().id,
                            version: r().version,
                          })
                        }
                      >
                        Archive
                      </button>
                    </Show>
                  </div>
                </div>

                <Show when={!editing()}>
                  <div class="card bg-base-100 shadow-sm">
                    <div class="card-body">
                      <h3 class="card-title text-base">Fields</h3>
                      <Show
                        when={Object.keys((r().data ?? {}) as Record<string, unknown>).length > 0}
                        fallback={<p class="text-sm opacity-60">No custom fields filled in yet.</p>}
                      >
                        <CustomFieldsRenderer
                          wsKey={props.wsKey}
                          customObjectDefinitionId={props.obj.id}
                          values={() => r().data}
                          onChange={() => {
                            /* read-only in view mode */
                          }}
                          variant="view"
                        />
                      </Show>
                    </div>
                  </div>
                </Show>

                <Show when={editing()}>
                  <form
                    onSubmit={save}
                    class="card bg-base-100 shadow-sm"
                    aria-label={`Edit ${props.obj.label_singular}`}
                  >
                    <div class="card-body flex flex-col gap-3">
                      <div>
                        <label class="label">
                          <span class="label-text">Name</span>
                        </label>
                        <input
                          class="input input-bordered w-full"
                          aria-label="Name"
                          value={form().name}
                          onInput={(e) => setForm((f) => ({ ...f, name: e.currentTarget.value }))}
                          required
                        />
                      </div>

                      <CustomFieldsRenderer
                        wsKey={props.wsKey}
                        customObjectDefinitionId={props.obj.id}
                        values={() => form().data}
                        onChange={(key, value) =>
                          setForm((f) => {
                            const next = { ...f.data };
                            if (
                              value === null ||
                              value === "" ||
                              (Array.isArray(value) && value.length === 0)
                            ) {
                              delete next[key];
                            } else {
                              next[key] = value;
                            }
                            return { ...f, data: next };
                          })
                        }
                      />

                      <div>
                        <label class="label">
                          <span class="label-text">Tags</span>
                        </label>
                        <TagsEditor
                          wsKey={props.wsKey}
                          value={() => form().tags}
                          onChange={(next) => setForm((f) => ({ ...f, tags: next }))}
                        />
                      </div>

                      <Show when={error()}>
                        <div role="alert" class="alert alert-error text-sm">
                          {error()}
                        </div>
                      </Show>

                      <div class="flex justify-end gap-2">
                        <button type="button" class="btn btn-ghost" onClick={cancel}>
                          Cancel
                        </button>
                        <button
                          type="submit"
                          class="btn btn-primary"
                          disabled={updateMutation.isPending}
                        >
                          {updateMutation.isPending ? "Saving…" : "Save"}
                        </button>
                      </div>
                    </div>
                  </form>
                </Show>

                <EntityTimeline
                  wsId={props.wsKey}
                  entityType="custom_record"
                  entityId={() => r().id}
                />
              </>
            );
          }}
        </Show>
      </div>
    </AppShell>
  );
}
